import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import {
  CommonResponse,
  OtpData,
} from '@app/core/interfaces/sign-in-result.interface';
import { ApiResponse } from '@app/core/models/api-response.model';
import {
  ProfileCoins,
  ProfileCompletionSummary,
  ProfileModel,
} from '@app/pages/shared/interfaces/user.model';
import { PasswordModel } from '@app/core/models/role.model';
import { environment } from '@environments/environment';
import { Observable, BehaviorSubject, tap, of, shareReplay } from 'rxjs';
import {
  ChangePassInput,
  UpdateInput,
  UserProfile,
} from '../interfaces/viewer-profile.interface';
import { UserCountryService } from '@app/public/shared/services/user-country.service';
import { ScratchRewardType } from '../../../core/enums/voucher.enum';
import {
  SavedPreference,
  Preferences,
  PreferencePayload,
  RewardsCredit,
  LanguagePreferenceStatus,
  LanguageList,
} from '@app/core/interfaces';
import { Reward } from '@app/core/interfaces/movie.interface';
import { SavedPreferencesPayload } from '@app/core/interfaces/preferences.interface';

@Injectable({
  providedIn: 'root',
})
export class ViewerProfileService {
  baseUrl: string = environment.apiUrl;

  isFilmmaker = signal(false);
  private profileSubject = new BehaviorSubject<ProfileModel | null>(null);
  public profile$ = this.profileSubject.asObservable();
  // Holds the in-flight GET so simultaneous callers share one request
  private profileFetch$: Observable<ApiResponse<ProfileModel>> | null = null;

  constructor(
    public http: HttpClient,
    private readonly userCountryService: UserCountryService,
  ) {}

  getProfile(): Observable<ApiResponse<ProfileModel>> {
    return this.http.get<any>(`${this.baseUrl}/user/profile`).pipe(
      tap((response: any) => {
        if (response?.data) {
          this.profileSubject.next(response.data);
        }
        if (
          response?.data?.countryCode &&
          response?.data?.countryCode?.trim() !== ''
        ) {
          this.userCountryService.setCountryCode(response.data.countryCode);
        }
      }),
    );
  }

  /**
   * Fetches the profile only if the in-memory cache is empty.
   * If data is already cached, returns it immediately as an observable
   * so callers always receive a value without firing a redundant HTTP request.
   * If a fetch is already in-flight, all callers share that single request
   * via shareReplay — no duplicate HTTP calls even on simultaneous init.
   */
  getProfileIfNeeded(): Observable<ApiResponse<ProfileModel>> {
    if (this.profileSubject.value !== null) {
      return of({
        data: this.profileSubject.value,
      } as ApiResponse<ProfileModel>);
    }
    if (!this.profileFetch$) {
      this.profileFetch$ = this.getProfile().pipe(
        tap(() => {
          this.profileFetch$ = null;
        }),
        shareReplay(1),
      );
    }
    return this.profileFetch$;
  }

  /** Clears the cached profile (call on logout). */
  clearProfile(): void {
    this.profileSubject.next(null);
    this.profileFetch$ = null;
  }

  sendNotificationInfo(isNotificationSend: boolean): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/user/profile?isNotificationSend=${isNotificationSend}`,
    );
  }

  changePassword(changeData: ChangePassInput): Observable<CommonResponse> {
    return this.http.put<CommonResponse>(
      `${this.baseUrl}/user/change-password`,
      changeData,
    );
  }

  setPassword(param: PasswordModel): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${this.baseUrl}/user/reset-password`,
      param,
    );
  }

  updateViewerProfile(
    updateInput: UpdateInput,
  ): Observable<ApiResponse<UserProfile>> {
    return this.http
      .put<
        ApiResponse<UserProfile>
      >(`${this.baseUrl}/user/profile`, updateInput)
      .pipe(
        tap((res) => {
          // Push updated data into cache so profile$ subscribers refresh
          // without firing an additional GET request.
          if (res?.data) {
            this.profileSubject.next(res.data as unknown as ProfileModel);
          }
        }),
      );
  }

  setIsFilmmaker(value: boolean) {
    this.isFilmmaker.set(value);
  }

  setCoinsRewardsDataForProfileCompletion(data: RewardsCredit): RewardsCredit {
    if (!this.isFilmmaker()) {
      return {
        isSuccess: data?.isSuccess ?? false,
        message: data?.message ?? '',
        rewards: data?.rewards ?? [],
      };
    }

    return {
      isSuccess: false,
      message: '',
      rewards: [],
    };
  }

  sendProfileOtp(otpData: OtpData): Observable<any> {
    const urlParams = new URLSearchParams(window.location.search);
    const debug = urlParams.get('debug') === 'true' ? '?debug=true' : '';
    return this.http.post<any>(
      `${this.baseUrl}/user/send-verification-otp${debug}`,
      otpData,
    );
  }

  sendVerifyEmail(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/user/send-verify-email`,
      payload,
    );
  }

  uploadProfilePicture(profilePic: any): Observable<any> {
    return this.http
      .put<any>(`${this.baseUrl}/user/profilePic`, profilePic)
      .pipe(
        tap((res) => {
          // Patch only the profilePicUrl in the cached profile so all subscribers
          // (navbar, viewer-profile) update without an extra GET.
          const current = this.profileSubject.value;
          if (current && res?.data?.profilePicUrl) {
            this.profileSubject.next({
              ...current,
              profilePicUrl: res.data.profilePicUrl,
            });
          }
        }),
      );
  }

  updateFinancialDetails(financeData: FormData): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/user/profile?type=FINANCE`,
      financeData,
    );
  }

  sendFCMToken(param: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/user/add/fcmToken`, param);
  }

  getPreferencesList(): Observable<ApiResponse<Preferences>> {
    return this.http.get<ApiResponse<Preferences>>(
      `${this.baseUrl}/user/preferencesLookup`,
    );
  }

  getSavedPreferences(): Observable<ApiResponse<SavedPreference>> {
    return this.http.get<ApiResponse<SavedPreference>>(
      `${this.baseUrl}/viewer/userPreference`,
    );
  }

  // No need for data hence given type any
  saveUserPreferences(
    payload: PreferencePayload,
  ): Observable<ApiResponse<SavedPreferencesPayload>> {
    return this.http.put<ApiResponse<SavedPreferencesPayload>>(
      `${this.baseUrl}/viewer/preferences`,
      payload,
    );
  }

  getStatusUserSelectedLanguage(): Observable<
    ApiResponse<LanguagePreferenceStatus>
  > {
    return this.http.get<ApiResponse<LanguagePreferenceStatus>>(
      `${this.baseUrl}/viewer/isPreferredLanguageSelected`,
    );
  }

  getLanguageList(): Observable<ApiResponse<LanguageList>> {
    return this.http.get<ApiResponse<LanguageList>>(
      `${this.baseUrl}/user/preferencesLookup?type=language`,
    );
  }

  setPreferredLanguage(languages: string[]): Observable<ApiResponse<any>> {
    return this.http.put<ApiResponse<any>>(
      `${this.baseUrl}/viewer/setPreferredLanguage`,
      { languages },
    );
  }
}
